import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { BaseService } from "@core/services/base.service";
import { LoadingService } from "@core/services/loading.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { BehaviorSubject, Observable } from "rxjs";

// Keep in sync with _breakpoints.scss
export enum Breakpoint {
  XXS_MIN = 0,
  XXS_MAX = 475.98,
  XS_MIN = 476,
  XS_MAX = 575.98,
  SM_MIN = 576,
  SM_MAX = 767.98,
  MD_MIN = 768,
  MD_MAX = 991.98,
  LG_MIN = 992,
  LG_MAX = 1199.98,
  XL_MIN = 1200,
  XL_MAX = 1399.98,
  XXL_MIN = 1400
}

@Injectable({
  providedIn: "root"
})
export class DeviceService extends BaseService {
  private readonly _isBrowser: boolean;
  private _pwaState = new BehaviorSubject<boolean>(false);
  public isPwaMode$: Observable<boolean> = this._pwaState.asObservable();

  constructor(
    public readonly loadingService: LoadingService,
    @Inject(PLATFORM_ID) public readonly platformId: Object,
    public readonly windowRefService: WindowRefService
  ) {
    super(loadingService);
    this._isBrowser = isPlatformBrowser(this.platformId);
    this._detectPwaMode();
  }

  /**
   * Check if the application is running in PWA mode
   * @returns boolean indicating if the app is in PWA mode
   */
  private _checkPwaMode(): boolean {
    if (!this._isBrowser) {
      return false;
    }

    const _window = this.windowRefService.nativeWindow;

    // Standard PWA detection
    const isStandalone = _window.matchMedia("(display-mode: standalone)").matches;
    const isFullscreen = _window.matchMedia("(display-mode: fullscreen)").matches;
    const isIosStandalone = (_window.navigator as any).standalone === true;

    // URL parameter detection for TWA (Trusted Web Activity)
    const urlParams = new URLSearchParams(_window.location.search);
    const isTwa = urlParams.get("utm_source") === "pwa" || urlParams.get("mode") === "twa";

    return isStandalone || isFullscreen || isIosStandalone || isTwa;
  }

  /**
   * Initialize PWA mode detection and set up listeners for changes
   */
  private _detectPwaMode(): void {
    // Set initial value
    this._pwaState.next(this._checkPwaMode());

    if (!this._isBrowser) {
      return;
    }

    // Listen for changes in display mode
    const _window = this.windowRefService.nativeWindow;
    _window.matchMedia("(display-mode: standalone)").addEventListener("change", e => {
      this._pwaState.next(e.matches || this._checkPwaMode());
    });
  }

  xxsMin(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth >= Breakpoint.XXS_MIN;
    }

    return false;
  }

  xxsMax(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth <= Breakpoint.XXS_MAX;
    }

    return false;
  }

  xsMin(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth >= Breakpoint.XS_MIN;
    }

    return false;
  }

  xsMax(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth <= Breakpoint.XS_MAX;
    }

    return false;
  }

  smMin(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth >= Breakpoint.SM_MIN;
    }

    return false;
  }

  smMax(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth <= Breakpoint.SM_MAX;
    }

    return false;
  }

  mdMin(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth >= Breakpoint.MD_MIN;
    }

    return false;
  }

  mdMax(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth <= Breakpoint.MD_MAX;
    }

    return false;
  }

  lgMin(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth >= Breakpoint.LG_MIN;
    }

    return false;
  }

  lgMax(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth <= Breakpoint.LG_MAX;
    }

    return false;
  }

  xlMin(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth >= Breakpoint.XL_MIN;
    }

    return false;
  }

  xlMax(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth <= Breakpoint.XL_MAX;
    }

    return false;
  }

  xxlMin(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const window = this.windowRefService.nativeWindow;
      return window.innerWidth >= Breakpoint.XXL_MIN;
    }

    return false;
  }

  isTouchEnabled(): boolean {
    if (!this._isBrowser) {
      return false;
    }

    const _window = this.windowRefService.nativeWindow;

    return (
      // Primary checks
      "ontouchstart" in _window ||
      navigator.maxTouchPoints > 0 ||
      // Secondary checks (media queries)
      (_window.matchMedia("(hover: none)").matches && _window.matchMedia("(pointer: coarse)").matches) ||
      // Older Android support
      (navigator as any).msMaxTouchPoints > 0
    );
  }

  isHybridPC(): boolean {
    if (!this._isBrowser) {
      return false;
    }

    const _window = this.windowRefService.nativeWindow;
    const hasTouch = "ontouchstart" in _window || navigator.maxTouchPoints > 0;
    const hasFinePointer = _window.matchMedia("(pointer: fine)").matches;
    const isLargeScreen = _window.matchMedia("(min-width: 1920px)").matches;

    return hasTouch && (hasFinePointer || isLargeScreen);
  }

  isMobile(): boolean {
    if (!this._isBrowser) {
      return false;
    }

    if (typeof navigator === "undefined") {
      return false;
    }

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  isPwa(): boolean {
    if (!this._isBrowser) {
      return false;
    }

    const _window = this.windowRefService.nativeWindow;

    // Standard PWA detection (works in Chrome, Edge, Samsung Internet, etc.)
    const isStandalone = _window.matchMedia("(display-mode: standalone)").matches;

    // Fullscreen mode detection (some PWAs use this)
    const isFullscreen = _window.matchMedia("(display-mode: fullscreen)").matches;

    // iOS Safari PWA detection
    const isIosStandalone = (navigator as any).standalone === true;

    // URL parameter detection for TWA (Trusted Web Activity)
    const urlParams = new URLSearchParams(_window.location.search);
    const isTwa = urlParams.get("utm_source") === "pwa" || urlParams.get("mode") === "twa";

    return isStandalone || isFullscreen || isIosStandalone || isTwa;
  }

  isAndroid(): boolean {
    if (!this._isBrowser) {
      return false;
    }

    if (typeof navigator === "undefined") {
      return false;
    }

    return /Android/i.test(navigator.userAgent);
  }

  getShareIcon(): IconProp {
    return this.isAndroid() ? "share-nodes" : "arrow-up-from-bracket";
  }

  offcanvasPosition(): "bottom" | "end" {
    return this.smMax() ? "bottom" : "end";
  }
}
